<template>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div class="space-y-8">
            <!-- Header -->
            <div>
                <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
            </div>

            <!-- Error State -->
            <div v-if="error" class="p-4 bg-error-50 border border-error-200 rounded-md">
                <p class="text-error-600">{{ error }}</p>
            </div>

            <!-- Dashboard Content -->
            <div v-else class="space-y-8">
                <!-- Overall Stats Section -->
                <section>
                    <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">Overall Stats - {{ totalGameCount }} Games</h2>
                    <div class="space-y-4">
                        <div v-for="username in orderedPlayerNames" :key="username" class="card">
                            <div class="card-body">
                                <div class="flex justify-between items-center mb-4">
                                    <h3 class="text-lg font-medium text-gray-900 dark:text-white">{{ username }}</h3>
                                    <span class="text-sm text-gray-500 dark:text-gray-400">Season Wins: {{ playerSeasonWins[username] || 0 }}</span>
                                </div>
                                <div class="grid grid-cols-4 sm:grid-cols-8 gap-2 sm:gap-4">
                                    <div class="stat-card">
                                        <div class="stat-value text-sm sm:text-base">{{ overview?.players[username].wins || 0 }}</div>
                                        <div class="stat-label text-xs">Wins</div>
                                    </div>
                                    <div class="stat-card">
                                        <div class="stat-value text-sm sm:text-base">{{ overview?.players[username].losses || 0 }}</div>
                                        <div class="stat-label text-xs">Losses</div>
                                    </div>
                                    <div class="stat-card">
                                        <div class="stat-value text-sm sm:text-base">{{ overview?.players[username].goals || 0 }}</div>
                                        <div class="stat-label text-xs">Goals</div>
                                    </div>
                                    <div class="stat-card">
                                        <div class="stat-value text-sm sm:text-base">{{ overview?.players[username].shots || 0 }}</div>
                                        <div class="stat-label text-xs">Shots</div>
                                    </div>
                                    <div class="stat-card">
                                        <div class="stat-value text-sm sm:text-base">{{ overview?.players[username].hits || 0 }}</div>
                                        <div class="stat-label text-xs">Hits</div>
                                    </div>
                                    <div class="stat-card">
                                        <div class="stat-value text-sm sm:text-base">{{ overview?.players[username].powerplay_goals || 0 }}/{{ overview?.players[username].powerplay_opportunities || 0 }}</div>
                                        <div class="stat-label text-xs">Power Play</div>
                                    </div>
                                    <div class="stat-card">
                                        <div class="stat-value text-sm sm:text-base">{{ overview?.players[username].powerplay_percentage || '0.0%' }}</div>
                                        <div class="stat-label text-xs">PP%</div>
                                    </div>
                                    <div class="stat-card">
                                        <div class="stat-value text-sm sm:text-base">{{ overview?.players[username].currentStreak || '-' }}</div>
                                        <div class="stat-label text-xs">Current Streak</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Recent Games Section -->
                <section>
                    <div class="flex justify-between items-center mb-4">
                        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Recent Games</h2>
                        <div class="flex items-center space-x-4">
                            <div class="flex items-center space-x-2">
                                <label class="text-sm text-gray-600 dark:text-gray-400">Sort:</label>
                                <select v-model="sortOrder" class="input text-sm">
                                    <option value="newest">Newest First</option>
                                    <option value="oldest">Oldest First</option>
                                </select>
                            </div>
                            <router-link to="/games" class="btn-secondary">
                                View All Games
                            </router-link>
                        </div>
                    </div>
                    
                    <div v-if="recentGames?.length === 0" class="card">
                        <div class="card-body text-center py-12">
                            <p class="text-gray-500 dark:text-gray-400">No games found</p>
                        </div>
                    </div>
                    
                    <div v-else class="space-y-4">
                        <div v-for="game in recentGames" :key="game.id" class="card hover:shadow-md transition-shadow">
                            <div class="card-body">
                                <div class="grid grid-cols-12 gap-4 items-center">
                                    <!-- Left: Game ID and Date -->
                                    <div class="col-span-2 flex items-center space-x-4 pl-2">
                                        <div class="text-2xl font-bold text-gray-400 dark:text-gray-500">
                                            {{ getDisplayGameId(game.id) }}
                                        </div>
                                        <div class="text-center">
                                            <div class="text-base font-bold text-gray-900 dark:text-white">
                                                {{ formatDateShort(game.date || game.created_at) }}
                                            </div>
                                            <div class="text-xs text-gray-500">
                                                {{ formatYear(game.date || game.created_at) }}
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Center: Game matchup -->
                                    <div class="col-span-8 flex items-center justify-center space-x-6">
                                        <!-- Away Team -->
                                        <div class="flex items-center space-x-3 min-w-[200px] justify-end">
                                            <div class="text-center">
                                                <div class="font-semibold text-gray-900 dark:text-white">{{ game.away_username }}</div>
                                                <div class="text-sm text-gray-600 dark:text-gray-300">{{ getTeamFullName(game.away_team) }}</div>
                                                <div class="text-xs text-gray-400">Away</div>
                                            </div>
                                            <img 
                                                :src="getTeamLogoUrl(game.away_team)" 
                                                :alt="`${game.away_team} logo`"
                                                class="w-8 h-8 object-contain"
                                                @error="($event.target as HTMLImageElement).style.display = 'none'"
                                            />
                                        </div>

                                        <!-- Score -->
                                        <div class="flex flex-col items-center">
                                            <div class="flex items-center space-x-4">
                                                <div class="text-3xl font-bold text-gray-900 dark:text-white">{{ game.away_score }}</div>
                                                <div class="text-gray-400 text-xl">—</div>
                                                <div class="text-3xl font-bold text-gray-900 dark:text-white">{{ game.home_score }}</div>
                                            </div>
                                            <!-- OT/SO Indicator centered under score -->
                                            <div v-if="game.is_overtime" class="mt-1">
                                                <span v-if="game.is_shootout" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                                    SO
                                                </span>
                                                <span v-else class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                                                    OT
                                                </span>
                                            </div>
                                        </div>

                                        <!-- Home Team -->
                                        <div class="flex items-center space-x-3 min-w-[200px] justify-start">
                                            <img 
                                                :src="getTeamLogoUrl(game.home_team)" 
                                                :alt="`${game.home_team} logo`"
                                                class="w-8 h-8 object-contain"
                                                @error="($event.target as HTMLImageElement).style.display = 'none'"
                                            />
                                            <div class="text-center">
                                                <div class="font-semibold text-gray-900 dark:text-white">{{ game.home_username }}</div>
                                                <div class="text-sm text-gray-600 dark:text-gray-300">{{ getTeamFullName(game.home_team) }}</div>
                                                <div class="text-xs text-gray-400">Home</div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Right: Details button -->
                                    <div class="col-span-2 flex items-center justify-end">
                                        <router-link 
                                            :to="`/game/${game.id}`" 
                                            class="btn-secondary text-sm"
                                        >
                                            Details →
                                        </router-link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { gameApi } from '@/services/api'
import { useNHLTeams } from '@/composables/useNHLTeams'
import { useTeamLogos } from '@/composables/useTeamLogos'

interface Game {
    id: number
    date: string
    created_at: string
    home_username: string
    away_username: string
    winner_username?: string
    home_team: string
    away_team: string
    home_score: number
    away_score: number
    is_overtime?: boolean
    is_shootout?: boolean
}

interface Player {
    username: string
    games_played: number
    wins: number
    losses: number
    goals: number
    shots: number
    hits: number
    powerplay_goals: number
    powerplay_opportunities: number
    powerplay_percentage: string
    currentStreak: string
}

interface Overview {
    players: Record<string, Player>
    totalGames: number
    availableYears: number[]
}

const error = ref<string | null>(null)
const overview = ref<Overview | null>(null)
const rawGames = ref<Game[]>([])
const sortOrder = ref('newest')
const playerSeasonWins = ref<Record<string, number>>({})

// Composables
const { getTeamFullName } = useNHLTeams()
const { getTeamLogoUrl } = useTeamLogos()

// Computed property that automatically sorts games when sortOrder or rawGames changes
const recentGames = computed(() => {
    if (!rawGames.value || rawGames.value.length === 0) return []
    
    return [...rawGames.value].sort((a, b) => {
        const dateA = new Date(a.created_at).getTime()
        const dateB = new Date(b.created_at).getTime()
        
        return sortOrder.value === 'newest' ? dateB - dateA : dateA - dateB
    })
})

// Computed property to order players with Aendy first, then Tobbba_
const orderedPlayerNames = computed(() => {
    if (!overview.value?.players) return []
    
    const playerNames = Object.keys(overview.value.players)
    const preferredOrder = ['Aendy', 'Tobbba_']
    
    // Filter and order players based on preferred order, then add any others
    const orderedPlayers = []
    
    // Add players in preferred order if they exist
    for (const name of preferredOrder) {
        if (playerNames.includes(name)) {
            orderedPlayers.push(name)
        }
    }
    
    // Add any remaining players not in the preferred order
    for (const name of playerNames) {
        if (!preferredOrder.includes(name)) {
            orderedPlayers.push(name)
        }
    }
    
    return orderedPlayers
})

// Computed property for total game count
const totalGameCount = computed(() => {
    if (!rawGames.value || rawGames.value.length === 0) {
        return overview.value?.totalGames || 0
    }
    return rawGames.value.length
})

const calculateSeasonWins = async () => {
    if (!overview.value?.availableYears) return

    const currentYear = new Date().getFullYear()
    const completedYears = overview.value.availableYears.filter(year => year < currentYear)
    
    const seasonWinCounts: Record<string, number> = {}
    
    try {
        for (const year of completedYears) {
            const yearOverviewResponse = await gameApi.getOverview(year)
            const yearOverview = yearOverviewResponse.data
            
            let maxWins = 0
            let winner = null
            
            for (const [username, player] of Object.entries(yearOverview.players)) {
                const wins = (player as Player).wins || 0
                if (wins > maxWins) {
                    maxWins = wins
                    winner = username
                }
            }
            
            if (winner) {
                seasonWinCounts[winner] = (seasonWinCounts[winner] || 0) + 1
            }
        }
        
        playerSeasonWins.value = seasonWinCounts
    } catch (err: any) {
        console.error('Failed to calculate season wins:', err)
    }
}

const fetchDashboardData = async () => {
    try {
        error.value = null

        const [overviewResponse, recentGamesResponse] = await Promise.all([
            gameApi.getOverview(),
            gameApi.getRecentGames(5)
        ])

        overview.value = overviewResponse.data
        rawGames.value = recentGamesResponse.data.games
        
        // Calculate season wins after getting overview data
        await calculateSeasonWins()
    } catch (err: any) {
        error.value = err.response?.data?.error || 'Failed to load dashboard data'
    }
}



const formatDateShort = (dateString: string) => {
    if (!dateString) return 'Unknown';
    
    // Handle both YYYY-MM-DD format and ISO timestamp format
    let date: Date;
    if (dateString.length === 10 && dateString.includes('-')) {
        // YYYY-MM-DD format - parse as UTC to avoid timezone shifts
        date = new Date(dateString + 'T00:00:00.000Z');
    } else {
        // ISO timestamp format
        date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) {
        return 'Invalid Date';
    }
    
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
    });
}

const formatYear = (dateString: string) => {
    if (!dateString) return 'Unknown';
    
    // Handle both YYYY-MM-DD format and ISO timestamp format
    let date: Date;
    if (dateString.length === 10 && dateString.includes('-')) {
        // YYYY-MM-DD format - parse as UTC to avoid timezone shifts
        date = new Date(dateString + 'T00:00:00.000Z');
    } else {
        // ISO timestamp format
        date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) {
        return 'Unknown';
    }
    
    return date.getUTCFullYear().toString();
}

const getDisplayGameId = (gameId: number) => {
    return gameId
}

onMounted(() => {
    fetchDashboardData()
})
</script>